/**
 * Arlo & Co — Canvas Capture
 * Intercepts Add to Cart on configured products, renders the customer's
 * personalisation text to a transparent PNG at InDesign-matched dimensions,
 * uploads it to a dedicated Uploadcare project, and attaches the CDN URL
 * as a hidden line-item property before submitting the cart form.
 *
 * Requires: arlo-product-config.js loaded first (generated by Arlo Admin)
 */

(function () {
  'use strict';

  const MM_PER_INCH = 25.4;

  // ── Wait for config to be available ────────────────────────────────────────
  function waitForConfig(cb, attempts) {
    attempts = attempts || 0;
    if (window.ArloProductConfig) return cb(window.ArloProductConfig);
    if (attempts > 20) return console.warn('[Arlo] Product config not found.');
    setTimeout(() => waitForConfig(cb, attempts + 1), 100);
  }

  // ── Entry point ─────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    waitForConfig(function (config) {
      // Get the current product handle from the page
      const handle = getProductHandle();
      if (!handle) return;

      const productConfig = config.products[handle];
      if (!productConfig || productConfig.outputFormat !== 'canvas-png') return;
      if (!config.uploadcareCanvasPngPublicKey) {
        console.warn('[Arlo] Canvas PNG Uploadcare public key not set. Skipping canvas capture.');
        return;
      }

      initCanvasCapture(productConfig, config.uploadcareCanvasPngPublicKey);
    });
  });

  function getProductHandle() {
    // Shopify sets this on window in most themes
    if (window.ShopifyAnalytics?.meta?.product?.handle) {
      return window.ShopifyAnalytics.meta.product.handle;
    }
    // Fallback: read from a meta tag or URL path
    const metaHandle = document.querySelector('meta[name="product-handle"]');
    if (metaHandle) return metaHandle.getAttribute('content');
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  // ── Set up form interception ────────────────────────────────────────────────
  function initCanvasCapture(productConfig, ucPublicKey) {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // Add a status overlay div for user feedback
    const statusEl = createStatusOverlay(form);

    // Expose a hook so arlo-font-selector.js can trigger a canvas preview update
    // (for future canvas preview rendering on font switch)
    window._arloTriggerCanvasUpdate = function () {
      // If there's a canvas preview element in the page, re-render it
      // For now this is a no-op stub — canvas preview is in the admin, not storefront
    };

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();

      try {
        showStatus(statusEl, 'Preparing your artwork…', 'loading');

        // 1. Collect field values from the form
        const fieldValues = collectFieldValues(form, productConfig.fields);

        // 2. Determine active font (from any font-selector the theme renders)
        const activeFontId = getActiveFont(form, productConfig);
        const fontConfig = productConfig.fonts.find(f => f.id === activeFontId) || productConfig.fonts[0];

        // 3. Load the font into the browser canvas context
        if (fontConfig) {
          showStatus(statusEl, 'Loading font…', 'loading');
          await loadFont(fontConfig.id, fontConfig.files, Number(fontConfig.sizePct));
        }

        // 4. Render each text field to its own canvas and upload
        showStatus(statusEl, 'Generating artwork…', 'loading');
        const artworkUrls = [];

        for (const field of productConfig.fields) {
          const text = fieldValues[field.id];
          if (!text) continue;

          const canvas = renderTextToCanvas(text, fontConfig, productConfig.canvasSettings);
          const blob = await canvasToBlob(canvas);
          const filename = `${handle()}-${field.id}-${Date.now()}.png`;

          showStatus(statusEl, 'Uploading artwork…', 'loading');
          const url = await uploadToUploadcare(blob, ucPublicKey, filename);
          artworkUrls.push({ fieldId: field.id, label: field.label, url });
        }

        // 5. Inject artwork URLs as visible line-item properties (shown as canvas thumbs in cart)
        //    and hide the raw text inputs so only the PNG preview shows
        artworkUrls.forEach(({ fieldId, label, url }) => {
          // Rename the raw text input to a hidden property (underscore = hidden in cart)
          const textInput = form.querySelector(
            `[name="properties[${label}]"], [name="properties[${fieldId}]"]`
          );
          if (textInput) {
            textInput.name = `properties[_text_${fieldId}]`;
          }
          // Inject the PNG URL under the original visible label — cart renders it as an image
          injectHiddenInput(form, `properties[${label}]`, url);
        });

        // 6. Also inject the font used
        if (fontConfig) {
          injectHiddenInput(form, 'properties[_artwork_font]', fontConfig.id);
        }

        hideStatus(statusEl);

        // 7. Submit the form normally
        form.submit();

      } catch (err) {
        console.error('[Arlo] Canvas capture error:', err);
        showStatus(statusEl, 'Could not generate artwork preview. Your order will proceed without it.', 'error');
        setTimeout(() => { hideStatus(statusEl); form.submit(); }, 3000);
      }
    });
  }

  // Cached handle reference for filename generation
  function handle() {
    return getProductHandle() || 'product';
  }

  // ── Collect form field values ───────────────────────────────────────────────
  function collectFieldValues(form, fields) {
    const values = {};
    fields.forEach(field => {
      // Look for Shopify line-item property inputs: name="properties[Label]"
      const input = form.querySelector(
        `[name="properties[${field.label}]"], [name="properties[${field.id}]"]`
      );
      if (input) values[field.id] = input.value.trim();
    });
    return values;
  }

  // ── Get the active font from the page ─────────────────────────────────────
  function getActiveFont(form, productConfig) {
    // Look for a font selector input (radio, select, or hidden set by theme JS)
    const fontInput = form.querySelector('[name*="font"], [name*="Font"], [data-arlo-font]');
    if (fontInput) {
      const val = fontInput.value;
      const match = productConfig.fonts.find(f => f.id === val || f.cssClass === val || f.name === val);
      if (match) return match.id;
    }
    // Default to first font
    return productConfig.fonts[0]?.id;
  }

  // ── Font loading ────────────────────────────────────────────────────────────
  async function loadFont(fontId, files, sizePct) {
    // Check if already loaded
    if (document.fonts.check(`12px '${fontId}'`)) return;

    for (const file of files) {
      try {
        const face = new FontFace(fontId, `url('${file.url}')`, sizePct && sizePct !== 100 ? { sizeAdjust: sizePct + '%' } : {});
        const loaded = await face.load();
        document.fonts.add(loaded);
        await document.fonts.ready;
        return;
      } catch (e) {
        // Try next file format
      }
    }
    console.warn(`[Arlo] Could not load font: ${fontId}`);
  }

  // ── Canvas rendering ────────────────────────────────────────────────────────
  function renderTextToCanvas(text, fontConfig, settings) {
    settings = settings || {};
    const dpi = settings.dpi || 300;
    const wMm = settings.templateWidthMm || 150;
    const hMm = settings.templateHeightMm || 50;
    const wPx = Math.round((wMm / MM_PER_INCH) * dpi);
    const hPx = Math.round((hMm / MM_PER_INCH) * dpi);
    const fontSize = settings.fontSize || Math.round(hPx * 0.4);
    const color = settings.color || '#000000';
    const vAlign = settings.vAlign || 'middle';
    const hAlign = settings.hAlign || 'center';
    const fontFamily = fontConfig ? `'${fontConfig.id}'` : 'sans-serif';

    const canvas = document.createElement('canvas');
    canvas.width = wPx;
    canvas.height = hPx;
    const ctx = canvas.getContext('2d');

    // Transparent background (default — ctx is already transparent)
    ctx.clearRect(0, 0, wPx, hPx);

    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textAlign = hAlign === 'center' ? 'center' : hAlign;
    ctx.textBaseline = vAlign === 'middle' ? 'middle' : vAlign;

    const x = hAlign === 'center' ? wPx / 2 : hAlign === 'left' ? fontSize * 0.5 : wPx - fontSize * 0.5;
    const y = vAlign === 'middle' ? hPx / 2 : vAlign === 'top' ? fontSize : hPx - fontSize * 0.3;

    ctx.fillText(text, x, y);
    return canvas;
  }

  // ── Canvas → Blob ───────────────────────────────────────────────────────────
  function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob returned null'));
      }, 'image/png');
    });
  }

  // ── Uploadcare upload ────────────────────────────────────────────────────────
  async function uploadToUploadcare(blob, publicKey, filename) {
    const formData = new FormData();
    formData.append('UPLOADCARE_PUB_KEY', publicKey);
    formData.append('UPLOADCARE_STORE', '1');
    formData.append('file', blob, filename || 'artwork.png');

    const res = await fetch('https://upload.uploadcare.com/base/', {
      method: 'POST',
      body: formData
    });

    if (!res.ok) throw new Error(`Uploadcare error: ${res.status}`);
    const data = await res.json();
    if (!data.file) throw new Error('Uploadcare returned no file UUID');

    return `https://ucarecdn.com/${data.file}/`;
  }

  // ── DOM helpers ─────────────────────────────────────────────────────────────
  function injectHiddenInput(form, name, value) {
    // Remove any existing input with this name first
    const existing = form.querySelector(`[name="${name}"]`);
    if (existing) existing.remove();

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  function createStatusOverlay(form) {
    const el = document.createElement('div');
    el.id = 'arlo-canvas-status';
    el.style.cssText = [
      'display:none',
      'position:fixed',
      'bottom:24px',
      'left:50%',
      'transform:translateX(-50%)',
      'background:#1a1a1a',
      'color:#fff',
      'padding:12px 24px',
      'border-radius:8px',
      'font-size:14px',
      'font-family:-apple-system,sans-serif',
      'z-index:9999',
      'box-shadow:0 4px 20px rgba(0,0,0,0.3)',
      'display:none',
      'align-items:center',
      'gap:10px',
      'max-width:90vw',
    ].join(';');
    document.body.appendChild(el);
    return el;
  }

  function showStatus(el, message, type) {
    const icon = type === 'loading'
      ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" style="animation:arlo-spin 0.8s linear infinite;flex-shrink:0"><circle cx="12" cy="12" r="10" opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>'
      : type === 'error'
        ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff6b6b" stroke-width="2" style="flex-shrink:0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
        : '';

    // Inject keyframe if not yet added
    if (!document.getElementById('arlo-spin-style')) {
      const style = document.createElement('style');
      style.id = 'arlo-spin-style';
      style.textContent = '@keyframes arlo-spin { to { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    }

    el.innerHTML = `${icon}<span>${message}</span>`;
    el.style.display = 'flex';
  }

  function hideStatus(el) {
    el.style.display = 'none';
  }

})();
